/* ==========================================================================
   GenuinePurity — New Header
   File: assets/new-header.js
   Handles: desktop mega-menus (hover + keyboard), search drawer,
            mobile drawer with sliding sub-panels.
   No dependencies. Safe to defer.
   ========================================================================== */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.gpInit === 'true') return;
    root.dataset.gpInit = 'true';

    /* ---------------- Desktop mega-menus ---------------- */
    var megaParents = root.querySelectorAll('[data-gp-mega-parent]');

    function openMega(parent) {
      closeSearch();
      megaParents.forEach(function (p) {
        if (p !== parent) closeMega(p);
      });
      parent.classList.add('is-open');
      var trigger = parent.querySelector('[data-gp-mega-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    function closeMega(parent) {
      parent.classList.remove('is-open');
      var trigger = parent.querySelector('[data-gp-mega-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    megaParents.forEach(function (parent) {
      var trigger = parent.querySelector('[data-gp-mega-trigger]');
      if (!trigger) return;

      /* Click toggles open/close (no navigation). */
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (parent.classList.contains('is-open')) {
          closeMega(parent);
        } else {
          openMega(parent);
        }
      });

      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          closeMega(parent);
          trigger.blur();
        }
      });
    });

    /* Close any open mega on outside click or Escape */
    document.addEventListener('click', function (e) {
      megaParents.forEach(function (parent) {
        if (!parent.contains(e.target)) closeMega(parent);
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') megaParents.forEach(closeMega);
    });

    /* ---------------- Cart: integrate with theme drawer + live count ---------------- */
    var cartLink = root.querySelector('[data-gp-cart]');
    var cartCountEl = root.querySelector('[data-gp-cart-count]');

    function updateCartCount(count) {
      if (!cartCountEl) return;
      cartCountEl.textContent = count;
      cartCountEl.classList.toggle('is-empty', Number(count) === 0);
      var link = cartCountEl.closest('[data-gp-cart]');
      if (link) link.setAttribute('aria-label', 'Cart (' + count + ' items)');
    }

    /* Re-read the cart from Shopify and refresh the bubble. Exposed globally so the
       theme's existing updateHeaderCartFromServer() hook (see theme.liquid) can call it too. */
    function refreshCartCount() {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { updateCartCount(cart.item_count); })
        .catch(function () {});
    }
    window.gpRefreshCartCount = refreshCartCount;
    if (typeof window.updateHeaderCartFromServer !== 'function') {
      window.updateHeaderCartFromServer = refreshCartCount;
    }

    /* If the theme runs a cart drawer, open it instead of navigating to /cart.
       We follow window.theme.settings.cartType (documented in this theme) and try the
       theme's own open hooks; if none are found we let the link fall through to /cart. */
    if (cartLink) {
      var themeCfg = (window.theme && window.theme.settings) || {};
      var cartType = cartLink.getAttribute('data-cart-type') || themeCfg.cartType || 'page';

      cartLink.addEventListener('click', function (e) {
        if (cartType !== 'drawer') return; /* page mode: normal navigation */

        var opened = false;

        /* 1) Theme's documented refresh/open helper */
        if (typeof window.refreshDrawer === 'function') {
          window.refreshDrawer();
          opened = true;
        }

        /* 2) Common custom-element / global patterns used by Enterprise-style themes */
        if (!opened) {
          var drawerEl = document.querySelector('cart-drawer, [data-cart-drawer], #cart-drawer, .cart-drawer, .drawer--cart, .js-cart-drawer');
          if (drawerEl) {
            if (typeof drawerEl.open === 'function') { drawerEl.open(); opened = true; }
            else if (typeof drawerEl.show === 'function') { drawerEl.show(); opened = true; }
            else {
              /* class/attribute toggles + a generic "open drawer" event the theme may listen for */
              drawerEl.classList.add('is-open', 'is-active', 'active', 'drawer--is-open');
              drawerEl.removeAttribute('hidden');
              document.dispatchEvent(new CustomEvent('cart:open'));
              document.dispatchEvent(new CustomEvent('drawer:open', { detail: { drawer: 'cart' } }));
              opened = true;
            }
          }
        }

        /* If we opened a drawer, prevent navigating away. Otherwise let the link work. */
        if (opened) e.preventDefault();
      });
    }

    /* Refresh the count whenever the theme signals a cart change. We listen broadly
       because different themes/apps emit different events; all are harmless no-ops if unused. */
    ['cart:updated', 'cart:refresh', 'cart:change', 'cart:build', 'rebuy.add', 'ajaxCart.afterCartLoad']
      .forEach(function (evt) { document.addEventListener(evt, refreshCartCount); });

    /* ---------------- Search drawer + predictive results ---------------- */
    var searchEl = root.querySelector('[data-gp-search]');
    var searchInput = root.querySelector('[data-gp-search-input]');
    var searchResults = root.querySelector('[data-gp-search-results]');

    function openSearch() {
      if (!searchEl) return;
      megaParents.forEach(closeMega);
      searchEl.hidden = false;
      if (searchInput) setTimeout(function () { searchInput.focus(); }, 30);
    }
    function closeSearch() {
      if (!searchEl) return;
      searchEl.hidden = true;
    }

    root.querySelectorAll('[data-gp-search-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        searchEl && searchEl.hidden ? openSearch() : closeSearch();
      });
    });
    root.querySelectorAll('[data-gp-search-close]').forEach(function (btn) {
      btn.addEventListener('click', closeSearch);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeSearch();
    });

    /* Predictive search — uses the theme's documented route (window.theme.routes.predictiveSearch).
       Degrades silently to plain form submission if the route isn't present. */
    var predictiveUrl = (window.theme && window.theme.routes && window.theme.routes.predictiveSearch) || '/search/suggest';
    var searchLimit = 4;
    var searchTimer = null;

    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function renderResults(products, query) {
      if (!searchResults) return;
      if (!products.length) {
        searchResults.innerHTML = '<p class="gp-search__results-empty">No matches for &ldquo;' + escapeHtml(query) + '&rdquo;.</p>';
        searchResults.hidden = false;
        return;
      }
      var grid = products.slice(0, searchLimit).map(function (p) {
        var img = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
        var media = img
          ? '<img src="' + img + '" alt="" width="220" height="165" loading="lazy">'
          : '<span class="gp-search__result-ph" aria-hidden="true"></span>';
        return '<a class="gp-search__result" href="' + p.url + '">' + media +
          '<span class="gp-search__result-title">' + escapeHtml(p.title) + '</span></a>';
      }).join('');
      searchResults.innerHTML = '<div class="gp-search__results-grid">' + grid + '</div>' +
        '<a class="gp-search__results-all" href="/search?q=' + encodeURIComponent(query) + '&type=product">View all results &rarr;</a>';
      searchResults.hidden = false;
    }

    function runPredictive(query) {
      /* Prefer the JSON API for clean data; fall back to nothing on error. */
      fetch(predictiveUrl + '?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=' + searchLimit, {
        headers: { 'Accept': 'application/json' }
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];
          renderResults(products, query);
        })
        .catch(function () { if (searchResults) searchResults.hidden = true; });
    }

    if (searchInput) {
      searchInput.addEventListener('input', function () {
        var q = searchInput.value.trim();
        clearTimeout(searchTimer);
        if (q.length < 2) {
          if (searchResults) { searchResults.hidden = true; searchResults.innerHTML = ''; }
          return;
        }
        searchTimer = setTimeout(function () { runPredictive(q); }, 220);
      });
    }

    /* ---------------- Mobile drawer ---------------- */
    var drawer = document.querySelector('[data-gp-drawer]');
    if (drawer) {
      var openBtns = document.querySelectorAll('[data-gp-drawer-open]');
      var closeBtns = drawer.querySelectorAll('[data-gp-drawer-close]');
      var subs = drawer.querySelectorAll('[data-gp-drawer-sub]');
      var expanders = drawer.querySelectorAll('[data-gp-drawer-expand]');
      var backBtns = drawer.querySelectorAll('[data-gp-drawer-back]');

      function openDrawer() {
        /* Offset the drawer below the header bar so the header stays visible. */
        var bar = root.querySelector('[data-gp-bar]');
        var top = bar ? bar.getBoundingClientRect().bottom : 0;
        drawer.style.setProperty('--gp-drawer-top', Math.max(0, Math.round(top)) + 'px');
        drawer.hidden = false;
        requestAnimationFrame(function () { drawer.classList.add('is-open'); });
        document.documentElement.style.overflow = 'hidden';
        openBtns.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      }
      function closeDrawer() {
        drawer.classList.remove('is-open');
        document.documentElement.style.overflow = '';
        openBtns.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
        setTimeout(function () {
          drawer.hidden = true;
          resetSubs();
        }, 400);
      }
      function resetSubs() {
        subs.forEach(function (s) { s.classList.remove('is-active'); s.hidden = true; });
      }

      openBtns.forEach(function (b) {
        b.addEventListener('click', function () {
          if (drawer.classList.contains('is-open')) {
            closeDrawer();
          } else {
            openDrawer();
          }
        });
      });
      closeBtns.forEach(function (b) { b.addEventListener('click', closeDrawer); });

      expanders.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var id = btn.getAttribute('data-gp-drawer-expand');
          var sub = drawer.querySelector('[data-gp-drawer-sub="' + id + '"]');
          if (sub) {
            sub.hidden = false;
            requestAnimationFrame(function () { sub.classList.add('is-active'); });
          }
        });
      });
      backBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var sub = btn.closest('[data-gp-drawer-sub]');
          if (sub) {
            sub.classList.remove('is-active');
            setTimeout(function () { sub.hidden = true; }, 400);
          }
        });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !drawer.hidden) closeDrawer();
      });
    }

    /* ---------------- Sticky ---------------- */
    /* Sticky must live on the outer Shopify section wrapper (.gp-header-section),
       because .gp-header's own parent is too short to stick within. When the schema
       "sticky" setting is on, add gp-sticky-on to that wrapper. */
    if (root.classList.contains('gp-header--sticky')) {
      var wrapper = root.closest('.gp-header-section') || root.parentElement;
      if (wrapper) wrapper.classList.add('gp-sticky-on');
    }
  }

  /* Initialize now and on Shopify theme-editor section load */
  document.querySelectorAll('[data-section-type="new-header"]').forEach(init);

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[data-section-type="new-header"]');
    if (section) init(section);
  });
})();